import { Ref, RefObject, useImperativeHandle, useRef } from 'react';

import { ScrollHandle } from '@dreryk/react-components';

import {
   DraftPrescriptionField,
   DraftPrescriptionsFieldArrayMethods,
} from './draftPrescriptions/DraftPrescriptionsForm.types';
import { mapMagistralDragFormDataToDraftMagistralDrugPrescription } from './draftPrescriptions/magistralDrugPrescription/DraftMagistralDrugPrescriptionField.utils';
import {
   mapPatientContinuouslyUsedMedicineToDraftStandardPrescription,
   mapPatientMedicineToDraftStandardPrescription,
} from './draftPrescriptions/standardPrescription/DraftStandardPrescriptionField.utils';
import { PrescriptionsApi } from './prescriptions.types';

type UsePrescriptionsApiReturnFn = {
   draftPrescriptionsFieldArrayMethodsRef: Ref<DraftPrescriptionsFieldArrayMethods>;
};

export const usePrescriptionsApi = (
   prescriptionsApiRef: Ref<PrescriptionsApi>,
   scrollRef: RefObject<ScrollHandle>
): UsePrescriptionsApiReturnFn => {
   const draftPrescriptionsFieldArrayMethodsRef = useRef<DraftPrescriptionsFieldArrayMethods>(null);

   const addDraftPrescription = (draftPrescription: DraftPrescriptionField): void => {
      const fieldArrayMethods = draftPrescriptionsFieldArrayMethodsRef.current;
      fieldArrayMethods?.append(draftPrescription, false);
      fieldArrayMethods?.move(fieldArrayMethods.fields.length, 0);
      scrollRef.current?.scrollToTop();
   };

   useImperativeHandle(prescriptionsApiRef, () => ({
      addDraftStandardPrescription: (patientId, patientMedicine, patientReimbursement) => {
         addDraftPrescription(
            mapPatientMedicineToDraftStandardPrescription(patientId, patientMedicine, patientReimbursement)
         );
      },
      addDraftStandardContinuouslyUsedMedicinePrescription: (
         patientId,
         patientMedicine,
         patientContinuouslyUsedMedicine
      ) => {
         addDraftPrescription(
            mapPatientContinuouslyUsedMedicineToDraftStandardPrescription(
               patientId,
               patientMedicine,
               patientContinuouslyUsedMedicine
            )
         );
      },
      addDraftMagistralDrugPrescription: (patientId, magistralDrug) => {
         addDraftPrescription(mapMagistralDragFormDataToDraftMagistralDrugPrescription(patientId, magistralDrug));
      },
   }));

   return {
      draftPrescriptionsFieldArrayMethodsRef: draftPrescriptionsFieldArrayMethodsRef,
   };
};
